// The reCAPTCHA official script (RECAPTCHA_SCRIPT_URL) is just a stupid wrapper of a few lines, that injects the effective reCAPTCHA
// script (https://www.gstatic.com/recaptcha/...), which is the one that implements window.grecaptcha.
// As a result the useScriptTag onLoaded callback is fired when the wrapper script is loaded, disregarding the status of the script
// that sets up recaptcha. The only way to ensure grecaptcha availability is by using a global callback referenced in the "onload"
// parameter (https://developers.google.com/recaptcha/docs/invisible?hl=en#javascript_resource_apijs_parameters).

import pDefer, { type DeferredPromise } from 'p-defer';

type GetToken = () => Promise<string | null>;
type FormatRequestOptions = (token: string | null) => Record<string, any>;

type UseInvisibleRecaptchaReturn = {
    getToken: GetToken;
    formatRequestOptions: FormatRequestOptions;
};

type Nullable<T> = T | null;

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';
const ONLOAD_CALLBACK_NAME = 'recaptchaScriptOnLoad';

const scriptUrl = `${RECAPTCHA_SCRIPT_URL}?${new URLSearchParams({ onload: ONLOAD_CALLBACK_NAME }).toString()}`;
const scriptsLoadedDefer = pDefer();

export const useInvisibleRecaptcha = (): UseInvisibleRecaptchaReturn => {
    const config = useRuntimeConfig();
    const { recaptchaDisabled, recaptchaV2SiteKey } = config.public;
    const { locale } = useI18n();

    let wrapperElement: Nullable<HTMLElement> = null;
    let widgetId: Nullable<number> = null;
    let getTokenDefer: Nullable<DeferredPromise<string>> = null;

    if (!recaptchaDisabled) {
        const { load } = useScriptTag(scriptUrl, undefined, {
            manual: true, // To avoid automatic script unload on unmount
        });

        onBeforeMount(() => {
            (window as any)[ONLOAD_CALLBACK_NAME] ??= (): void => {
                scriptsLoadedDefer.resolve();
            };

            load();
        });

        onBeforeUnmount(() => {
            wrapperElement?.remove();
        });

        onMounted(async () => {
            wrapperElement = document.createElement('div');
            document.body.appendChild(wrapperElement);

            await scriptsLoadedDefer.promise;

            widgetId = window.grecaptcha.render(wrapperElement, {
                sitekey: recaptchaV2SiteKey,
                size: 'invisible',
                hl: locale.value,
                callback: (token: string): void => {
                    getTokenDefer?.resolve(token);
                },
                'error-callback': (): void => {
                    getTokenDefer?.reject();
                },
                'expired-callback': (): void => {
                    getTokenDefer = null;
                },
            });
        });
    }

    const getToken: GetToken = async () => {
        if (recaptchaDisabled) return null;

        await scriptsLoadedDefer.promise;

        const recaptchaResolved = !!getTokenDefer && (await isResolved(getTokenDefer.promise));
        const recaptchaRejected = !!getTokenDefer && (await isRejected(getTokenDefer.promise));

        if (!getTokenDefer || recaptchaRejected || recaptchaResolved) {
            // BSD-235 Token must be one shot. A previous token cannot be reused, backend reports "TimeoutOrDuplicate" error.
            if (recaptchaResolved) window.grecaptcha.reset(widgetId);

            getTokenDefer = pDefer();
            window.grecaptcha.execute(widgetId);
        }

        return getTokenDefer.promise;
    };

    const formatRequestOptions: FormatRequestOptions = token => {
        if (recaptchaDisabled) return {};

        if (!token) throw new Error('Error in reCAPTCHA formatRequestOptions: nullish token');

        return { headers: { 'g-recaptcha-response': token } };
    };

    return { getToken, formatRequestOptions };
};
